import React, { useState, useEffect } from 'react';
import { Header, Button, Loader } from 'semantic-ui-react';
import { Link } from 'react-router-dom';

import { DealStates } from '~/constants';
import { indexRfisByProjectId, indexRfisByPortfolioId } from '~/api/requestForInformation';
import { fetchPortfolio } from '~/api/portfolios';
import { fetchProject } from '~/api/projects';
import { useProjectList } from '~/requests/projects/useProjectList';
import { usePortfolioList } from '~/requests/projects/usePortfolioList';
import { useUserContext } from '~/hooks';

interface DashboardChecklistItem {
    key: string;
    title: string;
    description: string;
    linkTo: string;
    isPortfolio: boolean;
}

const DashboardChecklist: React.FC = () => {
    const [checklistItems, setChecklistItems] = useState<DashboardChecklistItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { loggedInUser } = useUserContext();

    const { data: projects, status: projectsStatus } = useProjectList();
    const { data: portfolios, status: portfoliosStatus } = usePortfolioList();

    useEffect(() => {
        if (projectsStatus === 'success' && portfoliosStatus === 'success') {
            checkForActionItems();
        }
    }, [projects, portfolios, projectsStatus, portfoliosStatus]);

    const checkForActionItems = async () => {
        setLoading(true);
        const actionItems: DashboardChecklistItem[] = [];

        try {
            // Check portfolios for action items (filter out archived)
            if (portfolios && portfolios.length > 0) {
                const activePortfolios = portfolios.filter((portfolio: any) =>
                    !portfolio.isArchived
                );
                for (const portfolio of activePortfolios) {
                    const needsAction = await checkPortfolioNeedsAction(portfolio);
                    if (needsAction) {
                        actionItems.push({
                            key: `portfolio-${portfolio.id}`,
                            title: `Action needed on the ${portfolio.name} deal`,
                            description: 'Please review the deal summary page for more information.',
                            linkTo: `/portfolio/${portfolio.id}`,
                            isPortfolio: true
                        });
                    }
                }
            }

            // Check standalone projects for action items (filter out archived and portfolio projects)
            if (projects && projects.length > 0) {
                const activeStandaloneProjects = projects.filter((project: any) =>
                    !project.isArchived &&
                    !project.portfolioId &&
                    project.status === 'MATCHING'
                );
                for (const project of activeStandaloneProjects) {
                    const needsAction = await checkProjectNeedsAction(project);
                    if (needsAction) {
                        actionItems.push({
                            key: `project-${project.id}`,
                            title: `Action needed on the ${project.name} deal`,
                            description: 'Please review the deal summary page for more information.',
                            linkTo: `/project/${project.id}`,
                            isPortfolio: false
                        });
                    }
                }
            }

            setChecklistItems(actionItems);
        } catch (error) {
            console.error('Error checking for action items:', error);
        }

        setLoading(false);
    };

    const checkPortfolioNeedsAction = async (portfolio: any): Promise<boolean> => {
        // Check if portfolio is in appropriate state for actions
        const portfolioState = portfolio?.portfolioState;
        if (
            portfolioState?.latestState >= DealStates.IN_DILIGENCE ||
            portfolioState?.latestState === DealStates.DECLINED ||
            portfolioState?.latestState < DealStates.IN_REVIEW ||
            portfolio?.isArchived
        ) {
            return false;
        }

        // Fetch full portfolio data to get nested projects
        let fullPortfolio;
        try {
            fullPortfolio = await fetchPortfolio(portfolio.id);
        } catch (error) {
            console.error('Error fetching full portfolio:', error);
            return false;
        }

        const biddingComplete = fullPortfolio?.projects && fullPortfolio.projects.length > 0 &&
            fullPortfolio.projects.every((project: any) =>
                (project.projectBids && project.projectBids.length > 0) ||
                project.status === 'DECLINED_TO_BID'
            );

        // Check RFIs
        let allRfisComplete = true;
        try {
            const rfis = await indexRfisByPortfolioId(portfolio.id);
            if (rfis && rfis.length > 0) {
                allRfisComplete = rfis.every((rfi: any) =>
                    Array.isArray(rfi.responses) && rfi.responses.length > 0
                );
            }
        } catch (error) {
            console.error('Error fetching portfolio RFIs:', error);
        }

        // Needs action if bidding is incomplete OR RFIs are incomplete
        return !biddingComplete || !allRfisComplete;
    };

    const checkProjectNeedsAction = async (project: any): Promise<boolean> => {
        // Check if project is in appropriate state for actions
        if (
            project?.isArchived ||
            project?.status === 'BID_REJECTED'
        ) {
            return false;
        }

        // Fetch full portfolio data to get nested projects
        let fullProject;
        try {
            fullProject = await fetchProject(project.id);
        } catch (error) {
            console.error('Error fetching full project:', error);
            return false;
        }

        // Check if project has bids
        const biddingComplete = fullProject?.projectBids && fullProject.projectBids.length > 0;

        // Check RFIs
        let allRfisComplete = true;
        try {
            const rfis = await indexRfisByProjectId(project.id);
            if (rfis && rfis.length > 0) {
                allRfisComplete = rfis.every((rfi: any) =>
                    Array.isArray(rfi.responses) && rfi.responses.length > 0
                );
            }
        } catch (error) {
            console.error('Error fetching project RFIs:', error);
        }

        // Needs action if bidding is incomplete OR RFIs are incomplete
        return !biddingComplete || !allRfisComplete;
    };

    if (loading || projectsStatus === 'loading' || portfoliosStatus === 'loading') {
        return (
            <div className="card dashboard-checklist-container">
                <Loader active inline='centered'></Loader>
            </div>
        );
    }

    return (
        <div className="card dashboard-checklist-container">
            <Header as='h2'>Action items</Header>
            <div>
                {checklistItems.map((item, index) => (
                    <div key={item.key} className="checklist-item incomplete">
                        <div className="checklist-content">
                            <Header as="h3">{item.title}</Header>
                            <p>{item.description}</p>
                        </div>
                        <Button as={Link} to={item.linkTo} primary size="small">
                            Go there now
                        </Button>
                    </div>
                ))}
                {checklistItems.length === 0 && loggedInUser.isInvestor && loggedInUser.investorFullAccess === false && (
                    <div className="checklist-item incomplete">
                        <div className="checklist-content">
                            <Header as="h3">Get in touch</Header>
                            <p>
                                Contact your Conductor representative to learn about deal access, listings eligibility, and how we can match you with opportunities.
                            </p>
                        </div>
                        <Button
                            as="a"
                            href={`mailto:${loggedInUser.companyOwner?.email ?? 'marc@conductor.solar'}`}
                            primary
                            size="small"
                        >
                            Contact us
                        </Button>
                    </div>
                )}
                {checklistItems.length === 0 && (!loggedInUser.isInvestor || loggedInUser.investorFullAccess !== false) && (
                    <div className="checklist-item incomplete">
                        <div className="checklist-content">
                            <Header as="h3">Check out Conductor Listings!</Header>
                            <p>Head over to our listings page to view and express interest in the latest deals on our platform. This will help us find you great matches.</p>
                        </div>
                        <Button as={Link} to="/listings" primary size="small">
                            Go there now
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DashboardChecklist;